import React, { useState } from 'react';
import { Send, User, Mail, Phone, MessageSquare, Briefcase } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { contact, googleSheetsFormEndpoint } from '../mock';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast({ title: 'Missing fields', description: 'Please fill in all required fields.' });
      return;
    }

    setIsSubmitting(true);
    try {
      await fetch(googleSheetsFormEndpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(form),
      });
      toast({ title: 'Request submitted!', description: 'Thank you. Our team will reach out within 24 hours.' });
      setForm({ name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      toast({ title: 'Unable to submit', description: 'Please call or email us directly and try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-widest">
            Free Consultation
          </div>
          <h2 className="mt-5 text-4xl md:text-5xl font-extrabold text-[#0b1a1a] leading-tight">
            Request a <span className="text-emerald-600">Quote</span>
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed text-lg">
            Tell us a bit about your needs — our team will reach out within 24 hours with a tailored proposal.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4 p-4 bg-emerald-50/60 rounded-2xl border border-emerald-100">
              <div className="w-11 h-11 rounded-xl bg-emerald-500 grid place-items-center"><Mail className="w-5 h-5 text-white" /></div>
              <div>
                <div className="text-xs uppercase font-bold text-slate-500">Email</div>
                <a href={`mailto:${contact.email}`} className="font-bold text-[#0b1a1a] hover:text-emerald-600">{contact.email}</a>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-emerald-50/60 rounded-2xl border border-emerald-100">
              <div className="w-11 h-11 rounded-xl bg-emerald-500 grid place-items-center"><Phone className="w-5 h-5 text-white" /></div>
              <div>
                <div className="text-xs uppercase font-bold text-slate-500">Phone</div>
                <a href={`tel:${contact.phone1.replace(/\\s/g, '')}`} className="font-bold text-[#0b1a1a] hover:text-emerald-600">{contact.phone1}</a>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} id="quote-form" className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-emerald-50 p-8">
          <div className="grid grid-cols-1 gap-5">
            <Field icon={User} label="Full Name *" value={form.name} onChange={(v)=>setForm({...form,name:v})} placeholder="Your name" />
            <Field icon={Mail} label="Email *" type="email" value={form.email} onChange={(v)=>setForm({...form,email:v})} placeholder="name@company.com" />
            <Field icon={Phone} label="Phone *" value={form.phone} onChange={(v)=>setForm({...form,phone:v})} placeholder="+91 xxxxx xxxxx" />
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Service Interest *</label>
              <div className="mt-2 relative">
                <Briefcase className="w-4 h-4 text-emerald-600 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                <select value={form.service} onChange={(e)=>setForm({...form,service:e.target.value})}
                        className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-emerald-400 focus:bg-white outline-none focus:ring-2 focus:ring-emerald-100 font-medium text-slate-700 appearance-none">
                  <option value="">Select a service...</option>
                  <option>CBG Plant</option><option>Bio-Diesel</option><option>Bio Ethanol</option>
                  <option>EcoSolar</option><option>EV Charging</option><option>Green Hydrogen</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Message</label>
              <div className="mt-2 relative">
                <MessageSquare className="w-4 h-4 text-emerald-600 absolute left-4 top-4 pointer-events-none" />
                <textarea rows="4" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})}
                          placeholder="Tell us more about your project..."
                          className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-emerald-400 focus:bg-white outline-none focus:ring-2 focus:ring-emerald-100 font-medium text-slate-700" />
              </div>
            </div>
            <button type="submit" disabled={isSubmitting} className="mt-2 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-full font-bold shadow-lg shadow-emerald-200 transition-all">
              {isSubmitting ? 'Sending...' : 'Send Request'} <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Field = ({ icon: Icon, label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</label>
    <div className="mt-2 relative">
      <Icon className="w-4 h-4 text-emerald-600 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-emerald-400 focus:bg-white outline-none focus:ring-2 focus:ring-emerald-100 font-medium text-slate-700" />
    </div>
  </div>
);

export default Contact;
